import {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useMutation } from '@apollo/client';
import { Map, List } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import { FileChecksum } from '@rails/activestorage/src/file_checksum';

import useBlobUpload from 'helpers/direct_upload/blob_upload';
import useRecognition from 'helpers/recognition';

import { DIRECT_UPLOAD_MUTATION, DIRECT_UPLOAD_RECOGNITION_RESPONSE_MUTATION } from 'queries/prompt/response';
import { DIRECT_UPLOAD_CAMPAIGN_MUTATION, DIRECT_UPLOAD_COMPANY_MUTATION } from 'queries/tenant/direct_upload';

const QUERY_MAP = {
  response: { query: DIRECT_UPLOAD_MUTATION, node: 'directUpload' },
  response_recognize: { query: DIRECT_UPLOAD_RECOGNITION_RESPONSE_MUTATION, node: 'directUpload' },
  campaign: { query: DIRECT_UPLOAD_CAMPAIGN_MUTATION, node: 'directUploadCampaign' },
  company: { query: DIRECT_UPLOAD_COMPANY_MUTATION, node: 'directUploadCompany' },
};

const DEFAULT_LEGACY_ACTIVE_STORAGE = ['response', 'response_recognize'];
const OBJECT_PLACEHOLDER = new Map({
  filename: '',
  contentType: '',
  byteSize: '',
  checksum: '',
});

function DirectUpload(blob, key, documentName, country) {
  const [state, setState] = useState(new Map());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recognitionProgress, setRecognitionProgress] = useState(0);

  const variable = state.get('variable', null);
  const metadata = state.get('metadata', null);

  const result = state.get('result', null);
  const processError = state.get('processError', null);
  const finished = state.get('result', false);

  const [setHash] = useBlobUpload();
  const [recognize] = useRecognition();
  const [directUpload, { data, error }] = useMutation(QUERY_MAP[key].query, {
    onError: (err) => {
      setState(state.set('result', { processError: { upload: true } }).set('finished', true));
      console.error('mutation-on-error', err);
    },
  });

  useEffect(() => {
    if (isEmpty(blob)) return;

    FileChecksum.create(blob, (err, checksum) => {
      const extension = blob.type.split('/').pop();
      const filename = blob.name ? blob.name : `file-${Date.now()}.${extension}`;

      if (err || checksum === '') {
        console.error('file-checksum-blob-record-error', err);
        Sentry.captureException(err, { tags: { filename, size: blob.size, type: blob.type } });
        return;
      }

      let newvar = OBJECT_PLACEHOLDER.set('checksum', checksum)
        .set('filename', filename)
        .set('byteSize', blob.size)
        .set('contentType', blob.type);

      if (!DEFAULT_LEGACY_ACTIVE_STORAGE.includes(key)) newvar = newvar.set('serviceName', 'awscdn');

      setState(state.set('variable', newvar));
    });
  }, [blob]);

  useEffect(() => {
    if (isEmpty(variable)) return;

    const vars = {
      variables: {
        file: [variable.toJS()],
      }
    };

    directUpload(vars);
  }, [variable]);

  useEffect(() => {
    if (!data) return;

    const { metadata } = data.tenant[QUERY_MAP[key].node];
    setState(state.set('metadata', metadata[0]));
  }, [data]);

  useEffect(() => {
    if (!error) return;

    setState(state.set('processError', { upload: true }).set('finished', true));
  }, [data]);

  useEffect(() => {
    if (!metadata) return;

    const promises = [setHash(metadata, blob, setUploadProgress)];

    if (metadata.recognition && metadata.recognition.url) {
      promises.push(recognize(metadata.recognition.url, JSON.parse(metadata.recognition.headers), JSON.parse(metadata.recognition.body), blob, documentName, country, setRecognitionProgress));
    }

    Promise.all(promises).then((values) => {
      if (values.length == 1) {
        setState(state.set('result', metadata).set('processError', { upload: values[0].error }).set('finished', true));
        return;
      }

      const error = {
        upload: values[0].error,
        recognition: values[1].error,
      }

      const result = {
        ...metadata,
        recognition: values[1],
      }

      setState(state.set('result', result).set('processError', error).set('finished', true));
    });
  }, [metadata]);

  const progress = useMemo(() => {
    if (!metadata) return 0;

    if (metadata.recognition) {
      return Math.round((uploadProgress / 2) + (recognitionProgress / 2));
    }

    return uploadProgress;
  }, [uploadProgress, recognitionProgress, metadata]);

  return [result, processError, finished, progress];
}

export default DirectUpload;
